import { parseISO } from 'date-fns';

import { mapAttributeValueFromResponse } from '@/modules/common/mappers/custom-attribute.mapper';
import { Manager } from '@/modules/projects/types/project';
import { Department } from '@/modules/settings/types/department';
import { Role } from '@/modules/settings/types/role';
import { Seniority } from '@/modules/settings/types/seniority';
import { TeamMemberInfoResponse } from '@/modules/team/types/dto/response/team-member-info-response';
import { TeamMemberTypeDTO } from '@/modules/team/types/dto/team-member-type.dto';
import { TeamMember, TeamMemberType } from '@/modules/team/types/team-member';

/**
 * Maps team member type from DTO to domain model
 */
const mapTeamMemberType = (type: TeamMemberTypeDTO): TeamMemberType => {
  switch (type) {
    case TeamMemberTypeDTO.PLACEHOLDER:
      return TeamMemberType.Placeholder;
    case TeamMemberTypeDTO.REGULAR_MEMBER:
      return TeamMemberType.TeamMember;
    default:
      return TeamMemberType.TeamMember;
  }
};

/**
 * Maps team member info response to domain model fields
 */
export const mapTeamMemberInfoFromResponse = (
  response: TeamMemberInfoResponse
): Partial<TeamMember> => {
  // Build location string if country and city are present
  const location =
    response.country && response.city ? `${response.city}, ${response.country}` : undefined;

  // Map department/role/seniority to domain models (simple structure with memberCount)
  const department: Department | undefined = response.department
    ? {
        id: response.department.id,
        name: response.department.name,
        memberCount: 0,
        isDeleted: response.department.isDeleted,
      }
    : undefined;

  const role: Role | undefined = response.role
    ? {
        id: response.role.id,
        name: response.role.name,
        memberCount: 0,
        isDeleted: response.role.isDeleted,
      }
    : undefined;

  const seniority: Seniority | undefined = response.seniority
    ? {
        id: response.seniority.id,
        name: response.seniority.name,
        memberCount: 0,
        isDeleted: response.seniority.isDeleted,
      }
    : undefined;

  // Map manager from OpsiumUserShortResponse to Manager
  const manager: Manager | undefined = response.manager
    ? {
        id: response.manager.id,
        email: response.manager.email,
        name: response.manager.name,
        profileImageUrl: response.manager.profileImageUrl,
        isDeleted: response.manager.isDeleted,
      }
    : undefined;

  return {
    name: response.name,
    email: response.email ?? undefined,
    type: mapTeamMemberType(response.type),
    isChampion: response.isChampion,
    // Store raw status fields for future use
    invitationStatus: response.invitationStatus,
    baseStatus: response.status,
    // Map related entities to domain models
    department,
    seniority,
    role,
    manager,
    tags: response.tags || [],
    membershipType: response.membershipType ?? undefined,
    avatarLink: response.profileImageUrl,
    // Location fields
    country: response.country,
    city: response.city,
    location,
    // Contact information
    phoneNumber: response.phoneNumber,
    // Role and management
    isManager: response.isManager,
    about: response.about,
    dateOfJoin: response.dateOfJoin ? parseISO(response.dateOfJoin) : null,
    // Custom attributes
    customAttributes: response.customAttributesValues?.map(mapAttributeValueFromResponse),
  };
};
